/**
 * Display primitives — design/09 §8: chip, badge, skeleton, empty state, plus the two
 * components that make §12.6 structural rather than aspirational.
 *
 * §12.6: "Every status and variance cue carries an icon or text label in addition to
 * colour; screens remain readable under a deuteranopia simulation."
 *
 * `StatusIndicator` and `DeltaValue` are the ONLY sanctioned way to show a status or a
 * variance, and neither can be constructed without its second carrier — the icon is
 * chosen from the status, not passed in, and the minus sign is produced by
 * the formatter. A feature that wants a red number has to come through here.
 */

import { useTranslation } from 'react-i18next';
import {
  CircleCheck, CircleMinus, CircleSlash, TriangleAlert, OctagonAlert, ArrowDown, ArrowUp, Minus, Inbox, LoaderCircle,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { formatCurrency, formatNumber, formatPercent } from '../lib/format';
import type { Polarity } from '../export/types';
import { Button } from './button';
import { useEnclosed } from './enclosure';
import { useReadInFlight } from './read-activity';

/**
 * Arguments for an ICU message.
 *
 * G6 test b makes building a sentence with `+` or a template literal a build failure, so a
 * catalogue key that needs a datum in the middle of it takes that datum as a NAMED
 * ARGUMENT and lets the translator decide where it goes. `{detail}`, `{status}`, `{rule}`:
 * the message is the translator's, the value is the caller's, and the word order is
 * neither's to hard-code.
 *
 * The type is deliberately narrow. A `ReactNode` here would let a caller assemble a
 * sentence out of elements, which is the same defect wearing a different hat.
 */
export type ICUValues = Readonly<Record<string, string | number>>;

// ── chip and badge ───────────────────────────────────────────────────────────

export interface ChipProps {
  readonly labelKey: string;
  readonly onRemove?: () => void;
  readonly removeLabelKey?: string;
}

/** A filter chip. `radius-chip` (2px) per §6, not the 4px control radius. */
export function Chip({ labelKey, onRemove, removeLabelKey = 'chip.remove' }: ChipProps): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <span className="bg-surface-sunken border-hairline text-label text-ink-primary inline-flex items-center gap-1 rounded-chip border px-2 py-1">
      {t(labelKey)}
      {onRemove && (
        <Button variant="ghost" size="sm" onClick={onRemove} aria-label={t(removeLabelKey)} className="size-4 p-0">
          <Minus size={10} aria-hidden />
        </Button>
      )}
    </span>
  );
}

export interface BadgeProps {
  readonly labelKey: string;
  /**
   * ICU arguments for `labelKey`, placed by the TRANSLATOR — `StatusIndicatorProps.values`'
   * reason. A badge whose label names a datum (an unrecognised state, say) otherwise renders
   * its placeholder literally.
   */
  readonly values?: ICUValues;
  readonly count?: number;
}

/** `type-micro` — the one non-Antonio uppercase in the system (§4.2). */
export function Badge({ labelKey, values, count }: BadgeProps): React.JSX.Element {
  const { t, i18n } = useTranslation();
  return (
    <span className="bg-surface-sunken border-hairline text-micro text-ink-secondary inline-flex items-center gap-1 rounded-chip border px-2 py-1 uppercase tracking-[0.04em]">
      {t(labelKey, values ?? {})}
      {count !== undefined && (
        <span className="font-numeric text-numeric tabular-nums">{formatNumber(count, i18n.language)}</span>
      )}
    </span>
  );
}

// ── status: colour plus icon plus label, always ──────────────────────────────

export const STATUS_LEVELS = ['good', 'warning', 'serious', 'critical'] as const;
export type StatusLevel = (typeof STATUS_LEVELS)[number];

/**
 * The icon is derived from the level, not supplied. §5.3 notes that `status-warning`
 * (1.79:1) and `status-serious` (2.57:1) sit below 3:1 on the light surface BY DESIGN,
 * "always shipped with an icon and a text label, so colour never carries the meaning
 * alone". If the icon were a prop it could be omitted, and the contrast shortfall
 * would become a genuine failure rather than a designed trade-off.
 */
const STATUS_ICON: Record<StatusLevel, LucideIcon> = {
  good: CircleCheck,
  warning: TriangleAlert,
  serious: TriangleAlert,
  critical: OctagonAlert,
};

const STATUS_COLOUR: Record<StatusLevel, string> = {
  good: 'text-status-good',
  warning: 'text-status-warning',
  serious: 'text-status-serious',
  critical: 'text-status-critical',
};

export interface StatusIndicatorProps {
  readonly level: StatusLevel;
  /** REQUIRED. The text label is the second carrier; there is no icon-only variant. */
  readonly labelKey: string;
  /**
   * ICU arguments for `labelKey`, placed by the TRANSLATOR.
   *
   * Same reason as `EmptyStateProps.values` below: a status whose sentence needs a datum —
   * a rule identifier the catalogue does not name, a threshold, a count — must be able to
   * carry it as an argument. Building that sentence with `+` is a build failure (G6 test
   * b), and it is a build failure because word order is not universal.
   */
  readonly values?: ICUValues;
  readonly size?: number;
}

export function StatusIndicator({ level, labelKey, values, size = 12 }: StatusIndicatorProps): React.JSX.Element {
  const { t } = useTranslation();
  const Icon = STATUS_ICON[level];
  /*
    THE COLOUR IS ON THE MARK, AND THE LABEL READS LIKE THE TEXT AROUND IT.
    Both were on the wrapper, which coloured the words too, and the words are where that
    breaks: `status-warning` measures 1.79:1 against the light surface and `status-serious`
    2.57:1. §5.3 records those two figures exactly and accepts them, because the indicator
    always carries an icon and a label "so colour never carries the meaning alone" — which
    answers WCAG 1.4.1. It does not answer 1.4.3, the contrast minimum, and a label nobody
    can read is not a second carrier of anything. axe failed 14 routes across every chapter
    on it.
    Nothing §5.3 asks for is given up: the colour is still there, the icon is still derived
    from the level rather than supplied, and the label is still mandatory. On the mark the
    hue is decoration — the SHAPE already separates the four levels, triangle from octagon
    from circle — and decoration is what 1.4.11 exempts.
    It became visible rather than appearing: on `main` this map named `text-color-status-*`,
    where the colour group is `status`, so it emitted nothing and the indicator had no colour
    at all. `lint:dead-classes` found that, the fix made the colour real, and the real colour
    turned out to be unreadable as text. The same sequence is why ADR 0092 exists.
  */
  return (
    <span className="text-label inline-flex items-center gap-1">
      {/* The colour is on a SPAN around the mark rather than on the `<svg>`. §12.6's check
          reads `el.className`, which is an `SVGAnimatedString` and not a string on an SVG
          element, so a colour put straight on the icon would sit on the one node that check
          structurally cannot see. Here the element carrying the colour is HTML and CONTAINS
          an icon, which is exactly what that check asks of it. */}
      <span className={`inline-flex ${STATUS_COLOUR[level]}`}>
        <Icon size={size} aria-hidden />
      </span>
      <span>{t(labelKey, values ?? {})}</span>
    </span>
  );
}

// ── variance: colour plus arrow plus sign ────────────────────────────────────

export interface DeltaValueProps {
  readonly value: number;
  /**
   * design/09 §5.3: "Direction of 'good' is configurable per measure and must be.
   * Sell-through up is good; markdown up is not; stock cover up is neither without a
   * target. Never hard-code green-means-up."
   *
   * Required, with no default. A default would BE the hard-coding the rule forbids.
   */
  readonly polarity: Polarity;
  readonly kind?: 'number' | 'percent' | 'currency';
  readonly currency?: string;
  /** Screen-reader label describing what the delta is against. */
  readonly comparisonLabelKey?: string;
}

export function DeltaValue({
  value, polarity, kind = 'number', currency, comparisonLabelKey,
}: DeltaValueProps): React.JSX.Element {
  const { t, i18n } = useTranslation();

  const formatted = (() => {
    if (kind === 'percent') return formatPercent(value, i18n.language);
    if (kind === 'currency') return formatCurrency(value, currency ?? 'USD', i18n.language);
    return formatNumber(value, i18n.language);
  })();
  // A change is zero when it READS zero: -0.001 rounds to «0», and an arrow or a colour beside
  // that «0» would contradict the figure it decorates (ADR 9061). No non-zero digit, no change.
  const zero = !/[1-9]/.test(formatted);

  const direction: 'positive' | 'negative' | 'neutral' = (() => {
    if (zero || polarity === 'none' || polarity === 'target-relative') return 'neutral';
    const better = polarity === 'higher-is-better' ? value > 0 : value < 0;
    return better ? 'positive' : 'negative';
  })();

  const colour = direction === 'positive'
    ? 'text-delta-positive'
    : direction === 'negative' ? 'text-delta-negative' : 'text-delta-neutral';

  // §5.2 rule 4 — a negative carries a minus sign, never parentheses (ADR 9061). The arrow
  // is a third carrier for the direction of change, which is not the same thing as the sign
  // of the number: a -3pt move on a lower-is-better measure is an improvement.
  // A ZERO change has no direction, so it has no arrow: it reads plain «0» (ADR 9061). A
  // `Minus` icon in front of it read as a minus sign («– 0») and confused a client.
  const Arrow = zero ? null : value > 0 ? ArrowUp : ArrowDown;

  return (
    <span
      className={`font-numeric text-numeric inline-flex items-center gap-1 tabular-nums ${colour}`}
      aria-label={comparisonLabelKey ? t(comparisonLabelKey, { value: formatted }) : undefined}
    >
      {Arrow && <Arrow size={10} aria-hidden />}
      <span>{formatted}</span>
    </span>
  );
}

// ── skeleton ─────────────────────────────────────────────────────────────────

/** G2 §2.6 — "Empty, loading, error and no-permission states designed for every
 *  surface — not left to the framework default."
 *
 *  **It says that it is loading, in the middle of the space it holds** (owner request and
 *  clarification 2026-10-24, `docs/adr/9075`). Grey bars alone read as a page with nothing on it.
 *  While its read is in flight, a skeleton of two or more rows is a region of a real height — most
 *  of the viewport for a page's main read (six rows or more), a section's height otherwise — with
 *  a spinner and «Caricamento…» beneath it, centred both ways, so which area is loading is
 *  unmistakable. The visible words are `aria-hidden`: the region's `role="status"` already says
 *  `state.loading` to a screen reader, once. A one-row skeleton (a figure inside a line) stays a
 *  bar unless `label` asks. A skeleton that is NOT busy — a page waiting for the planner to choose
 *  something — never claims to be loading, and keeps its grey bars. Under `prefers-reduced-motion`
 *  the spinner does not turn and the bars do not pulse. */
export function Skeleton({ rows = 1, className, busy, label, extent }: {
  readonly rows?: number;
  readonly className?: string;
  /**
   * Whether the data this holds the place of is being read. Absent: asked of the query client
   * (`./read-activity.ts`) — a skeleton over a page waiting for a choice is not busy (C7).
   */
  readonly busy?: boolean;
  /** Whether the centred «Caricamento…» is drawn while busy. Default: from two rows. */
  readonly label?: boolean;
  /** How tall the pending region stands. Default: `page` from six rows, `section` from two, else `inline`. */
  readonly extent?: LoadingExtent;
}): React.JSX.Element {
  const { t } = useTranslation();
  const reading = useReadInFlight();
  const isBusy = busy ?? reading;
  const labelled = isBusy && (label ?? rows >= 2);
  if (labelled) {
    const size = extent ?? (rows >= 6 ? 'page' : rows >= 2 ? 'section' : 'inline');
    return (
      <div
        role="status"
        aria-live="polite"
        aria-busy
        data-loading-region={size}
        className={`flex w-full items-center justify-center ${LOADING_EXTENT[size]} ${className ?? ''}`}
      >
        <span className="sr-only">{t('state.loading')}</span>
        <LoadingIndicator />
      </div>
    );
  }
  return (
    <div role="status" aria-live="polite" aria-busy={isBusy} className={className}>
      <span className="sr-only">{t('state.loading')}</span>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="bg-surface-sunken mb-1 h-4 rounded-chip motion-safe:animate-pulse" />
      ))}
    </div>
  );
}

/** How tall a pending region stands: a page's main read, a section, a figure in a line. */
export type LoadingExtent = 'page' | 'section' | 'inline';

const LOADING_EXTENT: Readonly<Record<LoadingExtent, string>> = {
  page: 'min-h-[60vh]',
  section: 'min-h-[200px]',
  inline: 'min-h-[64px]',
};

/**
 * The visible loading cue: a spinner with «Caricamento…» beneath it, centred — the caller centres
 * it in the space the content will fill. Hidden from assistive technology, because the status
 * region around it already says it. Still under reduced motion.
 */
export function LoadingIndicator(): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <span aria-hidden="true" data-loading-indicator="" className="text-ink-secondary flex flex-col items-center gap-2 text-center text-[13px] leading-5">
      <LoaderCircle size={24} className="shrink-0 motion-safe:animate-spin" aria-hidden />
      <span>{t('state.loadingProgress')}</span>
    </span>
  );
}

// ── empty, error and no-permission states ────────────────────────────────────

export interface EmptyStateProps {
  readonly titleKey: string;
  readonly bodyKey: string;
  /**
   * ICU arguments for `titleKey` and `bodyKey`.
   *
   * Without this the designed error surface could not state a failure that carries a
   * datum — `api.problem.unknown` takes `{detail}`, `api.problem.malformed` takes
   * `{status}` — and five chapters independently wrote a local `ProblemNotice` rather
   * than concatenate one (G6 test b). The values are placed by the TRANSLATOR: they are
   * arguments to a message, never fragments joined onto one.
   *
   * Applied to both keys, because a title and a body describing the same failure
   * frequently want the same datum and there is no case for two argument bags.
   */
  readonly values?: ICUValues;
  readonly action?: { readonly labelKey: string; readonly onClick: () => void };
  readonly icon?: LucideIcon;
  /**
   * `not-built` — ADR 0015's amendment: the service answered on purpose that this read does
   * not exist yet. Not an error (nothing failed, nothing to retry, no `role="alert"`), and not
   * an empty state (an empty list would say something false about the season). Neutral icon.
   */
  readonly variant?: 'empty' | 'error' | 'no-permission' | 'not-built';
  /**
   * Whether this state owns the region it is in, or sits inside a panel that has already
   * introduced itself.
   *
   * `standalone` is the 404 and anything filling the content region: its title in the
   * display face, START-aligned on the page header's edge and anchored at the top. `inline`
   * is the overwhelmingly common case — `AsyncRegion` rendering it inside a `Panel` — where
   * the panel's own `<h2>` is already a condensed uppercase heading and a second one beneath
   * it is two headings stacked.
   *
   * Standalone was CENTRED until audit v4 G-10. Under a start-aligned kicker and title, a
   * centred block reads as off-axis, and on the twenty-four screens that are one empty read
   * it was the first thing a planner saw. It has no horizontal padding of its own: the router
   * body is `px-8`, the header's inset, so its text lands on the title's edge (ADR 9006's
   * amendment, audit v4 G-16).
   *
   * Unstated, it is read from the surroundings (`./enclosure.tsx`): `inline` inside a
   * container that has named itself, `standalone` otherwise. State it only to override.
   */
  readonly placement?: 'standalone' | 'inline';
  /**
   * The guided tour's anchor for this state (`data-tour`, ADR 9044). `ProblemNotice` sets
   * `page-status` on the state it draws where no page strip took it, so the tour's step about
   * "what state the page is in" finds it either way. A literal at the call site.
   */
  readonly tourAnchor?: string;
}

export function EmptyState({
  titleKey, bodyKey, values, action, icon, variant = 'empty', placement: stated, tourAnchor,
}: EmptyStateProps): React.JSX.Element {
  const { t } = useTranslation();
  // Stated wins; otherwise the container answers. Inside a `Panel` that is `inline`, on the
  // page it is `standalone` — see `./enclosure.tsx`.
  const enclosed = useEnclosed();
  const placement = stated ?? (enclosed ? 'inline' : 'standalone');
  const Icon = icon ?? (variant === 'empty' ? Inbox
    : variant === 'error' ? OctagonAlert
      : variant === 'not-built' ? CircleSlash
        : CircleMinus);
  const inline = placement === 'inline';
  return (
    <div
      data-tour={tourAnchor}
      role={variant === 'error' ? 'alert' : undefined}
      className={inline
        // Start-aligned, and a quarter of the padding. A centred block inside a panel whose
        // every other child is start-aligned reads as a layout fault rather than as a state.
        ? 'text-ink-secondary flex flex-col items-start gap-2 py-4 text-start'
        : 'text-ink-secondary flex flex-col items-start gap-2 py-8 text-start'}
    >
      {/* An empty state is chrome, not data — §3 places the brand in "logo, header,
          navigation, section headers, EMPTY STATES, buttons". The icon is ink, not
          brand red, because the colour rule is narrower than the placement rule. */}
      <Icon size={24} aria-hidden className="text-ink-muted" />
      {/*
        INSIDE A PANEL THE TITLE IS NOT A DISPLAY HEADING. A `Panel` already renders an
        `<h2>` in Antonio uppercase at `type.section`; this rendered a second one, in the
        same face at the same size, directly beneath it — two condensed uppercase headings
        stacked, saying different things, on what ADR 9004 admits is the most-seen screen
        state in a product whose database is empty. It also produced an H1 → H3 jump that
        axe reports as `heading-order` on every screen composing one.

        Inline it is `type.subsection`: DM Sans, 15/600, sentence case. Still a heading,
        still in the document outline, and no longer competing with the panel's own.
      */}
      <h3 className={inline
        ? 'text-subsection text-ink-primary'
        : 'text-section text-ink-primary font-display uppercase'}
      >
        {t(titleKey, values ?? {})}
      </h3>
      <p className="text-body max-w-prose">{t(bodyKey, values ?? {})}</p>
      {action && (
        <Button variant="primary" onClick={action.onClick}>{t(action.labelKey)}</Button>
      )}
    </div>
  );
}
